// src/components/OperatingAoiPicker.jsx
import { ScanEye, ChevronDown } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { fetchWithAuth } from "../api/fetchWithAuth";

// ── A CONTROLLED COMPONENT, NOT A STORE-BOUND ONE ───────────────────────────
//
// This used to read and write useOperatingAoiContext directly and fire the
// imagery catalog reload itself. Both had to go for a second caller to exist.
//
// useOperatingAoiContext is a GLOBAL SINGLETON shared with the map's imagery
// scope. With the picker bound to it, WellFinder Review's queue filter and the
// map's imagery scope would have been THE SAME VARIABLE: filtering the review
// queue to an AOI would silently re-scope the map, and picking an imagery AOI
// would re-filter someone's review queue. They are different questions that
// happen to be answered by the same table.
//
// The loadCatalog effect went for the same reason — in Review it would fetch an
// imagery catalog nobody is looking at, on every AOI change.
//
// So: selection is `value` / `onChange`, the list is `aois`, and each caller
// owns the store binding and the side effects it wants. TopNav keeps the
// context and the catalog reload; QueueFilters keeps neither.
//
// @param {object[]=} aois     entries to list. When omitted the component
//                             fetches /api/v1/operating-aois itself, which is
//                             the map's list — WellFinder passes its own,
//                             because it needs record counts the map's
//                             projection does not carry.
// @param {string|null} value  selected AOI id, or null for "all".
// @param {(id: string|null) => void} onChange
// @param {string=} allLabel   what the reset row says. "All Org Assets" is the
//                             map's sentence and is wrong for a review queue.
// @param {(aoi: object) => string=} renderMeta  right-aligned annotation, e.g.
//                             a record count. Returning falsy renders nothing.
export default function OperatingAoiPicker({
  aois: aoisProp,
  value = null,
  onChange,
  allLabel = "All Org Assets",
  renderMeta,
}) {
  const [fetched, setFetched] = useState([]);
  const operatingAois = aoisProp ?? fetched;
  const activeOperatingAoiId = value;

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // ── Load the AOI list on mount, ONLY when the caller did not supply one ──
  const selfLoads = aoisProp === undefined;
  useEffect(() => {
    if (!selfLoads) return;
    let cancelled = false;
    async function load() {
      const res = await fetchWithAuth("/api/v1/operating-aois");
      if (!res.ok || cancelled) return;
      const data = await res.json();
      if (!cancelled) setFetched(Array.isArray(data.operating_aois) ? data.operating_aois : []);
    }
    load();
    return () => { cancelled = true; };
  }, [selfLoads]);

  // ── Click-outside to close ──
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const activeAoi = operatingAois.find((a) => a.id === activeOperatingAoiId);
  const isGlobal = !activeOperatingAoiId;
  const selectedName = activeAoi?.name ?? "Select Operating AOI…";

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`
          flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs transition
          ${isGlobal
            ? "border-slate-600/60 bg-slate-700/20 text-gray-300 hover:bg-slate-700/30"
            : "border-accent-action/40 text-accent-action hover:bg-accent-action/10"}
        `}
      >
        <ScanEye size={12} className="shrink-0" />
        <span className="font-medium max-w-[160px] truncate">
          {isGlobal ? allLabel : selectedName}
        </span>
        <ChevronDown size={11} className="opacity-60 shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 w-64 bg-bg-soft border border-slate-700
                        rounded-md shadow-xl z-50 overflow-hidden">
          {/* Reset row — equivalent to "No project (global)" */}
          <div
            onClick={() => {
              onChange?.(null);
              setOpen(false);
            }}
            className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer
                       hover:bg-bg text-gray-400 hover:text-gray-200
                       border-b border-slate-800 transition"
          >
            <span className="w-2 h-2 rounded-full bg-slate-600 shrink-0" />
            {allLabel}
          </div>

          {operatingAois.length === 0 ? (
            <div className="px-3 py-3 text-xs text-fg-secondary italic">
              No operating AOIs available
            </div>
          ) : (
            operatingAois.map((aoi, i) => {
              const colors = ["#1f9dff", "#16f2d4", "#facc15", "#a78bfa"];
              return (
                <div
                  key={aoi.id}
                  onClick={() => {
                    onChange?.(aoi.id);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer
                             hover:bg-bg text-gray-300 hover:text-gray-100 transition
                             border-b border-slate-800/60 last:border-b-0"
                >
                  <span
                    className="w-2 h-2 rounded-sm shrink-0"
                    style={{ background: colors[i % colors.length] }}
                  />
                  <span className="flex-1 truncate">{aoi.name}</span>
                  {/* The annotation, when the caller supplies one. WellFinder
                      renders "4,647 of 10,800" here: one of the three AOIs
                      contains every record, so without the count a reviewer
                      cannot tell a real narrowing from a no-op that looks like
                      a choice. tabular-nums so the counts line up down the
                      list rather than jittering. */}
                  {renderMeta?.(aoi) ? (
                    <span className="text-[9px] text-fg-secondary tabular-nums shrink-0">
                      {renderMeta(aoi)}
                    </span>
                  ) : aoi.is_default ? (
                    <span className="text-[9px] text-fg-secondary font-mono uppercase tracking-wide shrink-0">
                      default
                    </span>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}